import { useState, useEffect } from 'react';
import { Search, Filter, Plus, Trash2, History } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import OrderCreateEditDialog from './OrderCreateEditDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p);
const getColombiaToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const normalizePaymentMethodKey = (m) => {
  const val = String(m || 'efectivo').trim().toLowerCase();
  if (val === 'cash' || val === 'efectivo' || !val) return 'efectivo';
  if (val === 'transfer') return 'trans_keiks';
  return val;
};

const statusLabels = { pending: 'Pendiente', approved: 'Aprobado', production: 'Produccion', delivery: 'Entrega', delivered: 'Entregado', cancelled: 'Cancelado' };
const statusColors = { pending: 'bg-yellow-100 text-yellow-800', approved: 'bg-blue-100 text-blue-800', production: 'bg-purple-100 text-purple-800', delivery: 'bg-orange-100 text-orange-800', delivered: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800' };

const orderActionLabels = {
  create_order: 'Creación de Pedido',
  update_order_status: 'Cambio de Estado',
  update_order: 'Edición de Pedido',
  delete_order: 'Eliminación',
  register_payment: 'Abono / Pago',
  delete_payment: 'Eliminación de Abono',
  add_order_novelty: 'Novedad Registrada'
};

const renderLogDetails = (log) => {
  const details = log.details;
  if (!details) return null;
  if (typeof details === 'string') return details;
  
  if (typeof details === 'object') {
    switch (log.action) {
      case 'update_order_status':
        return (
          <span>
            Cambio de estado: <span className="font-semibold capitalize">{statusLabels[details.new_status] || details.new_status}</span>
            {details.previous_status && (
              <span className="text-[#75664d] text-[11px] ml-1">
                (antes: {statusLabels[details.previous_status] || details.previous_status})
              </span>
            )}
          </span>
        );
      case 'create_order':
        return (
          <span>
            Pedido creado exitosamente {details.total ? `por ${formatPrice(details.total)}` : ''}
            {details.store_name ? ` • Sede: ${details.store_name}` : ''}
          </span>
        );
      case 'register_payment':
        return (
          <span className="text-green-700 font-semibold">
            Abono registrado: +{formatPrice(details.amount)} ({details.method || 'efectivo'})
          </span>
        );
      case 'delete_payment':
        return (
          <span className="text-red-700 font-medium">
            Abono eliminado: -{formatPrice(details.amount)}
          </span>
        );
      case 'add_order_novelty':
        return (
          <span className="text-amber-900 italic">
            "{details.text}"
          </span>
        );
      case 'update_order':
        return (
          <span>
            Información del pedido actualizada
          </span>
        );
      case 'delete_order':
        return (
          <span className="text-red-700">
            Pedido eliminado
          </span>
        );
      default:
        return (
          <span className="text-[11px] text-[#75664d]">
            {Object.entries(details)
              .filter(([k]) => !['order_id', 'tracking_code', 'customer_name'].includes(k))
              .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
              .join(' • ') || 'Acción registrada'}
          </span>
        );
    }
  }

  return String(details);
};

export default function AdminOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [orderCategoryFilter, setOrderCategoryFilter] = useState('all');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Dialog and details state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [editingOrder, setEditingOrder] = useState(null);

  // Payment registration modal state
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('efectivo');
  const [payDate, setPayDate] = useState(getColombiaToday());

  // Novelty States
  const [selectedOrderForNovelty, setSelectedOrderForNovelty] = useState(null);
  const [noveltyText, setNoveltyText] = useState('');
  const [savingNovelty, setSavingNovelty] = useState(false);

  // Order audit logs
  const [orderLogs, setOrderLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleDeleteOrder = async (order) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar permanentemente el pedido #${order.tracking_code}? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/orders/${order.id}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success(`Pedido #${order.tracking_code} eliminado`);
      if (selectedOrderDetails?.id === order.id) {
        setSelectedOrderDetails(null);
      }
      fetchOrders();
    } catch (err) {
      console.error(err);
      const detail = err.response?.data?.detail || 'Error al eliminar el pedido';
      toast.error(detail);
    }
  };

  const handleDeletePayment = async (orderId, paymentIndex) => {
    if (!window.confirm('¿Estás seguro de eliminar este pago registrado?')) return;
    try {
      const { data } = await axios.delete(`${API}/admin/orders/${orderId}/payments/${paymentIndex}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success('Pago eliminado con éxito');
      if (selectedOrderDetails && selectedOrderDetails.id === orderId) {
        setSelectedOrderDetails(prev => ({
          ...prev,
          payments: data.payments,
          amount_paid: data.amount_paid
        }));
      }
      fetchOrders();
    } catch (err) {
      console.error(err);
      toast.error('Error al eliminar el pago');
    }
  };

  const handleAddNovelty = async (e) => {
    e.preventDefault();
    if (!selectedOrderForNovelty) return;
    if (!noveltyText.trim()) {
      toast.error('Escribe el detalle de la novedad');
      return;
    }
    setSavingNovelty(true);
    try {
      const { data } = await axios.post(`${API}/admin/orders/${selectedOrderForNovelty.id}/novelties`, { text: noveltyText }, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success('Novedad registrada con éxito');
      const updatedNovelties = [...(selectedOrderForNovelty.novelties || []), data.novelty];
      setSelectedOrderForNovelty(prev => ({ ...prev, novelties: updatedNovelties }));
      if (selectedOrderDetails?.id === selectedOrderForNovelty.id) {
        setSelectedOrderDetails(prev => ({ ...prev, novelties: updatedNovelties }));
      }
      setNoveltyText('');
      fetchOrders();
    } catch (err) {
      console.error(err);
      toast.error('Error al guardar la novedad');
    } finally {
      setSavingNovelty(false);
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search) params.search = search;
      const { data } = await axios.get(`${API}/admin/orders`, { withCredentials: true, headers: getHeaders(), params });
      setOrders(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStoresAndProducts = async () => {
    try {
      let storesData = [];
      try {
        const adminStoresRes = await axios.get(`${API}/admin/stores`, { withCredentials: true, headers: getHeaders() });
        storesData = adminStoresRes.data;
      } catch {
        const publicStoresRes = await axios.get(`${API}/stores?include_hidden=true`);
        storesData = publicStoresRes.data;
      }
      const productsRes = await axios.get(`${API}/admin/products`, { withCredentials: true, headers: getHeaders() });
      setStores(storesData);
      setProducts(productsRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchStoresAndProducts();
  }, [statusFilter]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status: newStatus }, { withCredentials: true, headers: { ...getHeaders(), 'Content-Type': 'application/json' } });
      toast.success(`Estado actualizado a ${statusLabels[newStatus]}`);
      fetchOrders();
    } catch {
      toast.error('Error al actualizar estado');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchOrders();
  };

  const handleRegisterPayment = async () => {
    if (!payAmount || Number(payAmount) <= 0) {
      toast.error('El monto debe ser mayor a 0');
      return;
    }
    try {
      await axios.post(`${API}/admin/orders/${selectedOrderForPayment.id}/payments`, {
        amount: Number(payAmount),
        method: payMethod,
        date: payDate
      }, { withCredentials: true, headers: { ...getHeaders(), 'Content-Type': 'application/json' } });
      toast.success('Pago registrado con éxito');
      setSelectedOrderForPayment(null);
      setPayAmount('');
      fetchOrders();
    } catch {
      toast.error('Error al registrar el pago');
    }
  };

  const openCreateDialog = () => {
    setEditingOrder(null);
    setDialogOpen(true);
  };

  const openEditDialog = (order) => {
    setEditingOrder(order);
    setDialogOpen(true);
  };

  // Fetch audit logs when opening order details modal
  useEffect(() => {
    if (selectedOrderDetails?.id) {
      setLoadingLogs(true);
      axios.get(`${API}/admin/orders/${selectedOrderDetails.id}/logs`, {
        withCredentials: true,
        headers: getHeaders()
      })
      .then(res => setOrderLogs(res.data || []))
      .catch(err => {
        console.error('Error fetching order logs:', err);
        setOrderLogs([]);
      })
      .finally(() => setLoadingLogs(false));
    } else {
      setOrderLogs([]);
    }
  }, [selectedOrderDetails?.id]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, search, orderCategoryFilter]);

  const filteredOrders = orders.filter(o => {
    if (orderCategoryFilter === 'all') return true;
    return o.items?.some(item => {
      const catalogProd = products.find(p => p.id === item.productId);
      const cat = catalogProd?.category || item.category || (item.productId === 'gift-promo' ? 'promocional' : 'tortas-personalizadas');
      return cat === orderCategoryFilter;
    });
  });

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / pageSize));
  const paginatedOrders = filteredOrders.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="animate-fade-in">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#46371c]">Pedidos</h1>
            <p className="font-body text-xs sm:text-sm text-[#75664d] mt-1">Gestión y seguimiento de pedidos de clientes</p>
          </div>
          <Button onClick={openCreateDialog} className="bg-[#46371c] hover:bg-[#2d2312] text-[#fcfbf9] rounded-full font-body text-xs sm:text-sm h-10 px-5">
            <Plus className="w-4 h-4 mr-2" /> Crear Pedido
          </Button>
        </div>

        {/* Filters and search */}
        <Card className="p-4 border border-[#e5dfd3] rounded-2xl mb-6 bg-[#fcfbf9]">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-3.5 text-[#75664d]" />
              <input
                type="text"
                placeholder="Buscar por cliente o código..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full h-11 pl-9 pr-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
              />
            </div>
            <div className="w-full sm:w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-11 rounded-xl border-[#e5dfd3]">
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                  <SelectItem value="approved">Aprobados</SelectItem>
                  <SelectItem value="production">Producción</SelectItem>
                  <SelectItem value="delivery">En Entrega</SelectItem>
                  <SelectItem value="delivered">Entregados</SelectItem>
                  <SelectItem value="cancelled">Cancelados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-56">
              <Select value={orderCategoryFilter} onValueChange={setOrderCategoryFilter}>
                <SelectTrigger className="h-11 rounded-xl border-[#e5dfd3]" data-testid="order-category-select">
                  <SelectValue placeholder="Filtrar por tipo de torta" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos / categorías</SelectItem>
                  <SelectItem value="tortas-especiales">Tortas Especiales</SelectItem>
                  <SelectItem value="tortas-personalizadas">Tortas Personalizadas</SelectItem>
                  <SelectItem value="tortas-tematicas">Tortas Temáticas</SelectItem>
                  <SelectItem value="mesa-dulces">Mesa de Dulces</SelectItem>
                  <SelectItem value="detalles-regalos">Detalles y Regalos</SelectItem>
                  <SelectItem value="tortas">Tortas</SelectItem>
                  <SelectItem value="refrigeradas">Refrigeradas</SelectItem>
                  <SelectItem value="refrigeradas-premium">Refrigeradas Premium</SelectItem>
                  <SelectItem value="clasicas">Clásicas</SelectItem>
                  <SelectItem value="saludables">Saludables</SelectItem>
                  <SelectItem value="grados">Grados</SelectItem>
                  <SelectItem value="comunion">Primera Comunión</SelectItem>
                  <SelectItem value="pinateria">Piñatería</SelectItem>
                  <SelectItem value="panaderia">Panadería</SelectItem>
                  <SelectItem value="bebidas">Bebidas</SelectItem>
                  <SelectItem value="cafeteria">Cafetería</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </form>
        </Card>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" /></div>
        ) : filteredOrders.length === 0 ? (
          <p className="text-center py-12 font-body text-sm text-[#75664d]">No se encontraron pedidos con los filtros seleccionados</p>
        ) : (
          <div className="bg-[#fcfbf9] rounded-2xl border border-[#e5dfd3] overflow-x-auto">
            <Table className="min-w-[850px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-heading font-semibold text-[#46371c]">Código</TableHead>
                  <TableHead className="font-heading font-semibold text-[#46371c]">Cliente</TableHead>
                  <TableHead className="font-heading font-semibold text-[#46371c]">Total</TableHead>
                  <TableHead className="font-heading font-semibold text-[#46371c]">Forma de Pago</TableHead>
                  <TableHead className="font-heading font-semibold text-[#46371c]">Fecha Entrega</TableHead>
                  <TableHead className="font-heading font-semibold text-[#46371c]">Estado</TableHead>
                  <TableHead className="font-heading font-semibold text-[#46371c]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedOrders.map((o) => {
                  const computedTotal = o.total || o.items?.reduce((acc, item) => acc + (item.subtotal * (item.quantity || 1)), 0) || 0;
                  const amountPaid = o.amount_paid || 0;
                  const surplus = amountPaid > computedTotal ? amountPaid - computedTotal : 0;
                  const pm = (o.payment_method || (o.payments?.[0]?.method) || 'transfer').toLowerCase();

                  return (
                    <TableRow key={o.id} className="hover:bg-[#f4f1ea]/30">
                      <TableCell className="font-body font-medium text-[#46371c]">#{o.tracking_code}</TableCell>
                      <TableCell className="font-body text-[#75664d]">{o.customer_name}</TableCell>
                      <TableCell className="font-body font-semibold text-[#46371c]">
                        {formatPrice(computedTotal)}
                        {surplus > 0 && (
                          <span className="block text-[10px] text-emerald-700 font-bold">
                            (Sobra {formatPrice(surplus)})
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {pm === 'online' || pm === 'wompi' || pm === 'epayco' || pm === 'gateway' || pm.includes('linea') || pm.includes('línea') ? (
                          <Badge className="bg-purple-100 text-purple-800 border-none font-body text-xs font-semibold">💳 Online ({pm})</Badge>
                        ) : pm === 'transfer' || pm === 'transferencia' || pm.includes('bancolombia') || pm.includes('nequi') ? (
                          <Badge className="bg-blue-100 text-blue-800 border-none font-body text-xs font-semibold">🏦 Transferencia</Badge>
                        ) : pm === 'cash' || pm === 'efectivo' ? (
                          <Badge className="bg-green-100 text-green-800 border-none font-body text-xs font-semibold">💵 Efectivo</Badge>
                        ) : pm === 'rappi' ? (
                          <Badge className="bg-orange-100 text-orange-800 border-none font-body text-xs font-semibold">🛵 Rappi</Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-800 border-none font-body text-xs font-semibold">{pm.toUpperCase()}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-body text-[#75664d]">{o.delivery_date || 'No definida'}</TableCell>
                      <TableCell>
                        <Badge className={`${statusColors[o.status]} border-none font-body text-xs font-semibold`}>
                          {statusLabels[o.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="flex gap-1.5 flex-wrap">
                        <Button variant="outline" size="sm" onClick={() => setSelectedOrderDetails(o)} className="rounded-xl border-[#e5dfd3] font-body text-xs text-[#46371c] hover:bg-[#f4f1ea]">Ver</Button>
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(o)} className="rounded-xl border-[#e5dfd3] font-body text-xs text-[#46371c] hover:bg-[#f4f1ea]">Editar</Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => { setSelectedOrderForNovelty(o); setNoveltyText(''); }} 
                          className="rounded-xl border-amber-200 bg-amber-50/60 font-body text-xs text-amber-900 hover:bg-amber-100 font-medium"
                        >
                          📌 Novedad {o.novelties?.length ? `(${o.novelties.length})` : ''}
                        </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          const bal = Math.max(0, (o.total || 0) - (o.amount_paid || 0));
                          setSelectedOrderForPayment(o);
                          setPayAmount(bal > 0 ? String(bal) : '');
                          setPayMethod(normalizePaymentMethodKey(o.payment_method));
                          setPayDate(getColombiaToday());
                        }} 
                        className="rounded-xl border-green-200 bg-green-50/50 font-body text-xs text-green-700 hover:bg-green-100"
                      >
                        Abonar
                      </Button>
                      <Select value={o.status} onValueChange={(s) => updateStatus(o.id, s)}>
                        <SelectTrigger className="w-32 h-8 rounded-xl text-xs border-[#e5dfd3]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pendiente</SelectItem>
                          <SelectItem value="approved">Aprobado</SelectItem>
                          <SelectItem value="production">Producción</SelectItem>
                          <SelectItem value="delivery">En Entrega</SelectItem>
                          <SelectItem value="delivered">Entregado</SelectItem>
                          <SelectItem value="cancelled">Cancelar</SelectItem>
                        </SelectContent>
                      </Select>
                      {user && user.role === 'admin' && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleDeleteOrder(o)} 
                          className="rounded-xl border-red-200 bg-red-50/50 font-body text-xs text-red-600 hover:bg-red-100"
                        >
                          Eliminar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              </TableBody>
            </Table>

            {/* Controles de paginación */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-[#e5dfd3] bg-[#fcfbf9] text-xs font-body text-[#75664d]">
              <div className="flex items-center gap-2">
                <span>Mostrar</span>
                <Select value={String(pageSize)} onValueChange={(val) => { setPageSize(Number(val)); setCurrentPage(1); }}>
                  <SelectTrigger className="h-8 w-20 rounded-lg border-[#e5dfd3] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span>por pág. • {Math.min((currentPage - 1) * pageSize + 1, filteredOrders.length)} - {Math.min(currentPage * pageSize, filteredOrders.length)} de {filteredOrders.length} pedidos</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="h-8 px-3 rounded-lg border-[#e5dfd3] text-xs"
                >
                  Anterior
                </Button>
                <span className="px-2 font-medium text-[#46371c]">
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="h-8 px-3 rounded-lg border-[#e5dfd3] text-xs"
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog open={!!selectedOrderDetails} onOpenChange={(open) => !open && setSelectedOrderDetails(null)}>
        <DialogContent className="max-w-6xl w-[95vw] bg-[#fcfbf9] max-h-[90vh] overflow-y-auto" data-testid="order-details-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#46371c]">
              Detalles del Pedido #{selectedOrderDetails?.tracking_code}
            </DialogTitle>
          </DialogHeader>
          {selectedOrderDetails && (
            <div className="space-y-6 mt-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Columna Izquierda: Datos del Cliente, Detalles de Entrega, Observaciones y Totales */}
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4 text-xs font-body text-[#75664d] border-b border-[#e5dfd3]/60 pb-4">
                  <div>
                    <h4 className="font-heading text-sm font-bold text-[#46371c] mb-2">Datos del Cliente:</h4>
                    <p><span className="font-semibold text-[#46371c]">Nombre:</span> {selectedOrderDetails.customer_name}</p>
                    <p><span className="font-semibold text-[#46371c]">Email:</span> {selectedOrderDetails.customer_email || 'No proporcionado'}</p>
                    <p className="flex items-center gap-1.5">
                      <span className="font-semibold text-[#46371c]">Teléfono:</span> {selectedOrderDetails.customer_phone}
                      <span className="inline-flex gap-2 ml-1">
                        <a href={`tel:${selectedOrderDetails.customer_phone}`} className="p-0.5 hover:bg-[#f4f1ea] rounded text-[#46371c]" title="Llamar">
                          📞
                        </a>
                        <a 
                          href={`https://wa.me/${selectedOrderDetails.customer_phone.replace(/\+/g, '').replace(/\s/g, '')}?text=${encodeURIComponent(`Hola ${selectedOrderDetails.customer_name}, te escribimos de Keiks Repostería para confirmar tu pedido #${selectedOrderDetails.tracking_code} 🎂`)}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="p-0.5 hover:bg-[#f4f1ea] rounded text-green-600 font-semibold" 
                          title="WhatsApp"
                        >
                          💬
                        </a>
                      </span>
                    </p>
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-bold text-[#46371c] mb-2">Detalles de Entrega:</h4>
                    <p><span className="font-semibold text-[#46371c]">Método:</span> {selectedOrderDetails.delivery_type === 'delivery' ? 'Domicilio' : 'Recoger en Tienda'}</p>
                    <p><span className="font-semibold text-[#46371c]">Sede:</span> {selectedOrderDetails.store_name}</p>
                    {selectedOrderDetails.delivery_type === 'delivery' && <p><span className="font-semibold text-[#46371c]">Dirección:</span> {selectedOrderDetails.delivery_address}</p>}
                    <p><span className="font-semibold text-[#46371c]">Fecha:</span> {selectedOrderDetails.delivery_date || 'No definida'}</p>
                    {selectedOrderDetails.delivery_time_slot && <p><span className="font-semibold text-[#46371c]">Horario:</span> {selectedOrderDetails.delivery_time_slot}</p>}
                    <p><span className="font-semibold text-[#46371c]">Estado:</span> <span className="font-bold">{statusLabels[selectedOrderDetails.status]}</span></p>
                  </div>
                </div>

                {selectedOrderDetails.observations && (
                  <div className="p-3 bg-[#fcf7ed] border border-[#e8c894] rounded-xl text-xs font-body">
                    <h4 className="font-heading text-xs font-semibold text-[#46371c] mb-1">Observaciones del Pedido:</h4>
                    <p className="text-[#855723]">{selectedOrderDetails.observations}</p>
                  </div>
                )}

                {/* Totales */}
                <div className="border-t border-[#e5dfd3] pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="font-body text-xs text-[#75664d] space-y-1">
                    <p><span className="font-semibold text-[#46371c]">Forma de Pago:</span> {selectedOrderDetails.payment_method || 'transfer'}</p>
                    <p><span className="font-semibold text-[#46371c]">Anticipo Requerido:</span> {formatPrice(selectedOrderDetails.deposit_required ?? (selectedOrderDetails.total * 0.5))}</p>
                    <p><span className="font-semibold text-green-700">Abonado / Pagado:</span> {formatPrice(selectedOrderDetails.amount_paid || 0)}</p>
                    <p><span className="font-semibold text-red-700">Saldo Pendiente:</span> {formatPrice(Math.max(0, (selectedOrderDetails.total || 0) - (selectedOrderDetails.amount_paid || 0)))}</p>
                    
                    {selectedOrderDetails.payments && selectedOrderDetails.payments.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-[#e5dfd3]/60 space-y-1.5">
                        <p className="font-semibold text-[#46371c]">Pagos Registrados:</p>
                        {selectedOrderDetails.payments.map((p, pIdx) => (
                          <div key={pIdx} className="flex items-center justify-between bg-white p-2 rounded-lg border border-[#e5dfd3] text-xs">
                            <div>
                              <span className="font-bold text-green-700">{formatPrice(p.amount)}</span>
                              <span className="text-[10px] text-[#75664d] ml-2">({p.method || 'efectivo'} - {p.date?.split('T')[0] || ''})</span>
                            </div>
                            {user && ['admin', 'vendedora', 'vendedor'].includes(user.role) && (
                              <Button
                                size="xs"
                                variant="ghost"
                                onClick={() => handleDeletePayment(selectedOrderDetails.id, pIdx)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 px-1.5 rounded-md"
                                title="Eliminar este Pago"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right space-y-1">
                    <div className="flex justify-between md:justify-end md:gap-4 text-xs text-[#75664d]">
                      <span>Subtotal Productos:</span>
                      <span>{formatPrice((selectedOrderDetails.total || 0) - (selectedOrderDetails.delivery_fee || 0))}</span>
                    </div>
                    {selectedOrderDetails.delivery_fee > 0 && (
                      <div className="flex justify-between md:justify-end md:gap-4 text-xs text-[#75664d]">
                        <span>Costo Domicilio:</span>
                        <span>{formatPrice(selectedOrderDetails.delivery_fee)}</span>
                      </div>
                    )}
                    <div className="flex justify-between md:justify-end md:gap-4 border-t border-[#e5dfd3] pt-1">
                      <span className="font-body text-xs font-semibold text-[#46371c]">Total del Pedido:</span>
                      <span className="font-heading text-lg font-bold text-[#46371c]">
                        {formatPrice(selectedOrderDetails.total || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Columna Derecha: Ítems del Pedido */}
              <div className="space-y-4">
                <h4 className="font-heading text-sm font-bold text-[#46371c]">Ítems del Pedido:</h4>
                <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
                  {selectedOrderDetails.items?.map((item, idx) => {
                    const catalogProd = products.find(p => p.id === item.productId);
                    const imageList = (item.image || catalogProd?.image || (item.productId === 'gift-promo' ? '/obsequio.webp' : 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=200')).split(',');
                    return (
                      <div key={idx} className="flex flex-col gap-3 p-3 bg-[#f4f1ea]/40 rounded-xl border border-[#e5dfd3] text-xs font-body text-[#75664d]">
                        <div className="flex gap-4">
                          <div className="flex gap-1.5 flex-wrap shrink-0">
                            {imageList.map((url, i) => (
                              <img key={i} src={url} alt="" className="w-16 h-16 rounded-lg object-cover border border-[#e5dfd3]" />
                            ))}
                          </div>
                          <div className="flex-1 space-y-1">
                            <p className="font-heading text-sm font-semibold text-[#46371c]">{item.name}</p>
                            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                              <p><span className="font-semibold text-[#46371c]">Tamaño:</span> {item.size || 'Estándar'}</p>
                              {item.flavor && <p><span className="font-semibold text-[#46371c]">Sabor:</span> {item.flavor}</p>}
                              {item.filling && <p><span className="font-semibold text-[#46371c]">Relleno:</span> {item.filling}</p>}
                              <p><span className="font-semibold text-[#46371c]">Cantidad:</span> {item.quantity || 1}</p>
                            </div>
                            {item.observations && <p className="text-[11px] text-[#46371c] bg-[#eef6f2] p-1 rounded mt-1 font-medium">Especificaciones: {item.observations}</p>}
                          </div>
                          <div className="text-right shrink-0">
                            <span className="font-heading text-sm font-semibold text-[#46371c] block">{formatPrice(item.subtotal * (item.quantity || 1))}</span>
                            <span className="text-[10px]">{formatPrice(item.subtotal)} c/u</span>
                          </div>
                        </div>
                        <div className="border-t border-[#e5dfd3]/60 pt-2 space-y-1 text-[10px]">
                          <div className="flex justify-between"><span>Precio Base:</span><span>{formatPrice(item.base_price ?? item.subtotal)}</span></div>
                          {item.flavor_price > 0 && <div className="flex justify-between"><span>Sabor ({item.flavor}):</span><span>+{formatPrice(item.flavor_price)}</span></div>}
                          {item.filling_price > 0 && <div className="flex justify-between"><span>Relleno ({item.filling}):</span><span>+{formatPrice(item.filling_price)}</span></div>}
                          {item.complements_with_prices?.map((comp, cIdx) => (
                            <div key={cIdx} className="flex justify-between"><span>{comp.name}:</span><span>+{formatPrice(comp.price)}</span></div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Historial de Novedades */}
                {selectedOrderDetails.novelties && selectedOrderDetails.novelties.length > 0 && (
                  <div className="mt-4 p-3.5 bg-amber-50/80 border border-amber-200 rounded-2xl text-xs font-body space-y-2">
                    <h4 className="font-heading text-xs font-bold text-amber-900 flex items-center gap-1.5 uppercase tracking-wide">
                      📌 Historial de Novedades ({selectedOrderDetails.novelties.length}):
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                      {selectedOrderDetails.novelties.map((nov, nIdx) => (
                        <div key={nIdx} className="bg-white p-2.5 rounded-xl border border-amber-200/80 shadow-xs text-[#46371c]">
                          <p className="font-medium text-amber-950">{nov.text}</p>
                          <div className="flex justify-between items-center text-[10px] text-[#75664d] mt-1.5 pt-1 border-t border-gray-100">
                            <span>Registrado por: <strong>{nov.author}</strong></span>
                            <span>{nov.created_at ? new Date(nov.created_at).toLocaleString('es-CO') : ''}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Auditoría y Movimientos del Pedido */}
            <div className="mt-6 pt-5 border-t border-[#e5dfd3]">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <History className="w-4 h-4 text-[#46371c]" />
                  <h4 className="font-heading text-sm font-bold text-[#46371c]">
                    Historial de Movimientos y Auditoría
                  </h4>
                </div>
                <span className="text-xs text-[#75664d]">
                  {orderLogs.length} movimiento{orderLogs.length === 1 ? '' : 's'} registrado{orderLogs.length === 1 ? '' : 's'}
                </span>
              </div>

              {/* Tarjeta de Creación */}
              <div className="bg-[#f4f1ea]/40 p-3 rounded-xl border border-[#e5dfd3] mb-3 text-xs font-body text-[#75664d] flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="font-semibold text-[#46371c]">Subido / Creado por: </span>
                  <span className="font-bold text-[#46371c]">
                    {selectedOrderDetails.creator_user_name || (selectedOrderDetails.created_by_admin ? 'Administración' : 'Cliente en Tienda Web')}
                  </span>
                  {selectedOrderDetails.creator_user_email && (
                    <span className="text-[11px] text-[#75664d] ml-1">({selectedOrderDetails.creator_user_email})</span>
                  )}
                  {selectedOrderDetails.creator_user_role && (
                    <Badge className="ml-2 bg-amber-100 text-amber-900 border-none text-[10px] py-0 px-1.5 font-semibold">
                      {selectedOrderDetails.creator_user_role}
                    </Badge>
                  )}
                </div>
                <div className="text-[11px] text-[#75664d]">
                  {selectedOrderDetails.created_at ? new Date(selectedOrderDetails.created_at).toLocaleString('es-CO') : ''}
                </div>
              </div>

              {/* Lista de Movimientos */}
              {loadingLogs ? (
                <div className="py-4 text-center text-xs text-[#75664d]">Cargando historial de movimientos...</div>
              ) : orderLogs.length > 0 ? (
                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {orderLogs.map((log, idx) => (
                    <div key={idx} className="bg-white p-2.5 rounded-xl border border-[#e5dfd3] text-xs font-body flex items-start justify-between gap-3 shadow-xs">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className="bg-[#f4f1ea] text-[#46371c] border-none text-[10px] font-semibold">
                            {orderActionLabels[log.action] || log.action}
                          </Badge>
                          {log.status && (
                            <Badge className={`text-[10px] border-none font-semibold ${statusColors[log.status] || 'bg-gray-100'}`}>
                              {statusLabels[log.status] || log.status}
                            </Badge>
                          )}
                        </div>
                        <div className="text-[#46371c] font-medium text-xs mt-0.5">
                          {renderLogDetails(log)}
                        </div>
                        <p className="text-[11px] text-[#75664d]">
                          Realizado por: <strong className="text-[#46371c]">{log.user_name || 'Sistema'}</strong>
                          {log.user_role && ` (${log.user_role})`}
                          {log.user_email && ` • ${log.user_email}`}
                        </p>
                      </div>
                      <span className="text-[10px] text-[#75664d] shrink-0 whitespace-nowrap">
                        {log.created_at ? new Date(log.created_at).toLocaleString('es-CO') : ''}
                      </span>
                    </div>
                  ))}
                </div>
              ) : selectedOrderDetails.status_history && selectedOrderDetails.status_history.length > 0 ? (
                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {selectedOrderDetails.status_history.map((sh, idx) => (
                    <div key={idx} className="bg-white p-2.5 rounded-xl border border-[#e5dfd3] text-xs font-body flex items-start justify-between gap-3 shadow-xs">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <Badge className={`text-[10px] border-none font-semibold ${statusColors[sh.status] || 'bg-gray-100'}`}>
                            {statusLabels[sh.status] || sh.status}
                          </Badge>
                        </div>
                        <p className="text-[11px] text-[#75664d]">
                          Cambiado por: <strong className="text-[#46371c]">{sh.user_name || 'Sistema'}</strong>
                          {sh.user_role && ` (${sh.user_role})`}
                          {sh.user_email && ` • ${sh.user_email}`}
                        </p>
                      </div>
                      <span className="text-[10px] text-[#75664d] shrink-0 whitespace-nowrap">
                        {sh.changed_at ? new Date(sh.changed_at).toLocaleString('es-CO') : ''}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-4 text-xs text-[#75664d] bg-gray-50 rounded-xl border border-dashed border-[#e5dfd3]">
                  No hay movimientos adicionales registrados para este pedido.
                </p>
              )}
            </div>
          </div>
          )}
          {selectedOrderDetails && (
            <div className="flex gap-3 justify-end mt-4 pt-4 border-t border-[#e5dfd3]">
              <Button 
                variant="outline" 
                onClick={() => { setSelectedOrderForNovelty(selectedOrderDetails); setNoveltyText(''); }} 
                className="rounded-xl border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 font-body text-xs font-semibold"
              >
                📌 Agregar Novedad
              </Button>
              {user && user.role === 'admin' && (
                <Button 
                  variant="outline" 
                  onClick={() => handleDeleteOrder(selectedOrderDetails)} 
                  className="rounded-xl border-red-200 bg-red-50 text-red-600 hover:bg-red-100 font-body text-xs font-semibold"
                >
                  Eliminar Pedido
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Novelties Modal */}
      <Dialog open={!!selectedOrderForNovelty} onOpenChange={(open) => !open && setSelectedOrderForNovelty(null)}>
        <DialogContent className="max-w-xl w-[95vw] bg-[#fcfbf9] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-[#46371c]">
              📌 Novedades del Pedido #{selectedOrderForNovelty?.tracking_code}
            </DialogTitle>
            <DialogDescription className="font-body text-xs text-[#75664d]">
              Registra una novedad o consulta el historial de observaciones de este pedido.
            </DialogDescription>
          </DialogHeader>

          {selectedOrderForNovelty && (
            <div className="space-y-4 mt-2">
              <form onSubmit={handleAddNovelty} className="space-y-3 bg-white p-4 border border-[#e5dfd3] rounded-2xl">
                <label className="font-body text-xs text-[#75664d] font-semibold block">Agregar Nueva Novedad</label>
                <textarea
                  rows={3}
                  value={noveltyText}
                  onChange={e => setNoveltyText(e.target.value)}
                  placeholder="Ej: El cliente solicitó cambiar la fecha de entrega o el mensaje del letrero..."
                  className="w-full p-3 rounded-xl border border-[#e5dfd3] font-body text-xs text-[#46371c] focus:outline-none focus:border-[#a0d5ba] resize-none"
                  required
                />
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={savingNovelty}
                    className="bg-[#46371c] text-white hover:bg-[#2d2312] rounded-xl text-xs h-9 px-4 font-body font-semibold"
                  >
                    {savingNovelty ? 'Guardando...' : 'Guardar Novedad'}
                  </Button>
                </div>
              </form>

              <div className="space-y-2">
                <h4 className="font-heading text-xs font-bold text-[#46371c] border-b border-[#e5dfd3]/60 pb-1 uppercase tracking-wide">
                  Historial de Novedades ({selectedOrderForNovelty.novelties?.length || 0})
                </h4>
                {selectedOrderForNovelty.novelties && selectedOrderForNovelty.novelties.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                    {selectedOrderForNovelty.novelties.map((nov, idx) => (
                      <div key={idx} className="p-3 bg-amber-50/70 border border-amber-200/80 rounded-xl text-xs font-body text-[#46371c]">
                        <p className="font-medium text-amber-950">{nov.text}</p>
                        <div className="flex justify-between items-center text-[10px] text-[#75664d] mt-1.5 pt-1 border-t border-amber-200/50">
                          <span>Registrado por: <strong>{nov.author}</strong></span>
                          <span>{nov.created_at ? new Date(nov.created_at).toLocaleString('es-CO') : ''}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-6 text-xs text-[#75664d] bg-gray-50 rounded-xl border border-dashed border-[#e5dfd3]">
                    No hay novedades registradas para este pedido.
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create / Edit Order Dialog (Isolated to eliminate input lag) */}
      <OrderCreateEditDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingOrder={editingOrder}
        stores={stores}
        products={products}
        onOrderSaved={fetchOrders}
        API={API}
        getHeaders={getHeaders}
      />

      {/* Dialog para Agregar Pago (Abono) */}
      <Dialog open={!!selectedOrderForPayment} onOpenChange={() => setSelectedOrderForPayment(null)}>
        <DialogContent className="max-w-md bg-white p-5 rounded-2xl border border-[#e5dfd3] space-y-4">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-[#46371c]">Agregar Pago / Abono</DialogTitle>
            <DialogDescription className="font-body text-xs text-[#75664d]">
              Registra un nuevo pago para el pedido #{selectedOrderForPayment?.tracking_code}
            </DialogDescription>
          </DialogHeader>

          {selectedOrderForPayment && (
            <div className="space-y-4">
              <div className="bg-[#fcfbf9] p-3 rounded-xl border border-[#e5dfd3]/50 text-xs font-body space-y-1">
                <div className="flex justify-between">
                  <span className="text-[#75664d]">Total Pedido:</span>
                  <span className="font-semibold text-[#46371c]">{formatPrice(selectedOrderForPayment.total || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#75664d]">Abonado hasta el momento:</span>
                  <span className="font-semibold text-green-700">{formatPrice(selectedOrderForPayment.amount_paid || 0)}</span>
                </div>
                <div className="flex justify-between border-t border-[#e5dfd3]/50 pt-1 mt-1">
                  <span className="font-bold text-[#46371c]">Saldo Pendiente:</span>
                  <span className="font-bold text-red-600">
                    {formatPrice(Math.max(0, (selectedOrderForPayment.total || 0) - (selectedOrderForPayment.amount_paid || 0)))}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Monto a Pagar (COP)</label>
                  <input
                    type="number"
                    value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)}
                    placeholder="Monto..."
                    className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                  />
                </div>

                <div>
                  <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Método de Pago</label>
                  <Select value={payMethod} onValueChange={setPayMethod}>
                    <SelectTrigger className="w-full h-10 rounded-xl border-[#e5dfd3] text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="efectivo">Efectivo</SelectItem>
                      <SelectItem value="trans_keiks">Trans Keiks</SelectItem>
                      <SelectItem value="trans_esteba">Trans Esteba</SelectItem>
                      <SelectItem value="datafono">Datáfono</SelectItem>
                      <SelectItem value="online">Online / Pasarela</SelectItem>
                      <SelectItem value="rappi">Rappi</SelectItem>
                      <SelectItem value="credito">Crédito</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="font-body text-xs text-[#75664d] mb-1 block font-medium">Fecha de Pago</label>
                  <input
                    type="date"
                    value={payDate}
                    onChange={(e) => setPayDate(e.target.value)}
                    className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setSelectedOrderForPayment(null)} className="flex-1 rounded-full border-[#e5dfd3] text-[#46371c] font-body">Cancelar</Button>
            <Button onClick={handleRegisterPayment} className="flex-1 bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body">
              Registrar Pago
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
